import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { DIAL_MODES, PURPOSES } from "@/lib/campaignMeta";
import { SfFilterBuilder } from "@/components/SfFilterBuilder";
import { SfSourcePicker } from "@/components/SfSourcePicker";
import { ScriptEditor } from "@/components/ScriptEditor";
import { X, CaretDown, CaretRight } from "@phosphor-icons/react";

const inputCls = "mt-1 w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-ink";
const lbl = "text-[11px] font-bold uppercase tracking-widest text-stone2";

export default function CampaignEditModal({ campaign, onClose, onSaved }) {
    const [agents, setAgents] = useState([]);
    const [saving, setSaving] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const kind = campaign.data_source?.kind || "";
    const isFilterSf = ["sf_leads", "sf_contacts", "existing_leads", "existing_contacts"].includes(kind);
    const isPickerSf = ["sf_listview", "sf_report", "sf_campaign"].includes(kind);
    const isSf = isFilterSf || isPickerSf;
    const [filters, setFilters] = useState(campaign.data_source?.filters || {});
    const [dataSource, setDataSource] = useState(campaign.data_source || {});
    const [form, setForm] = useState({
        name: campaign.name || "",
        description: campaign.description || "",
        purpose: campaign.purpose || "sales",
        dialing_mode: campaign.dialing_mode || "preview",
        agent_ids: campaign.assignment?.agent_ids || [],
        recording_enabled: campaign.recording_enabled !== false,
        pacing_ratio: campaign.pacing_ratio ?? 2,
        abandon_threshold: campaign.abandon_threshold ?? 0.03,
        abandon_message: campaign.abandon_message ?? "",
        wh_enabled: campaign.working_hours?.enabled || false,
        wh_open: campaign.working_hours?.open || "09:00",
        wh_close: campaign.working_hours?.close || "18:00",
        retry_attempts: campaign.retry?.attempts ?? 2,
        talking_points: campaign.talking_points || [],
        objections: campaign.objections || [],
    });
    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    useEffect(() => {
        api.get("/users").then(({ data }) => setAgents(data.filter((u) => ["agent", "supervisor"].includes(u.role)))).catch(() => {});
    }, []);

    const toggleAgent = (id) => set({ agent_ids: form.agent_ids.includes(id) ? form.agent_ids.filter((x) => x !== id) : [...form.agent_ids, id] });

    const save = async () => {
        if (!form.name.trim()) { toast.error("Name is required"); return; }
        setSaving(true);
        try {
            await api.patch(`/campaigns/${campaign.id}`, {
                name: form.name.trim(),
                description: form.description,
                purpose: form.purpose,
                dialing_mode: form.dialing_mode,
                assignment: { ...(campaign.assignment || {}), agent_ids: form.agent_ids },
                working_hours: { ...(campaign.working_hours || {}), enabled: form.wh_enabled, open: form.wh_open, close: form.wh_close },
                retry: { ...(campaign.retry || {}), attempts: Number(form.retry_attempts) },
                recording_enabled: form.recording_enabled,
                pacing_ratio: Number(form.pacing_ratio),
                abandon_threshold: Math.max(0, Math.min(1, Number(form.abandon_threshold) || 0.03)),
                abandon_message: form.abandon_message || "",
                talking_points: form.talking_points,
                objections: form.objections,
                ...(isSf ? { data_source: { ...(campaign.data_source || {}), filters } } : {}),
            });
            toast.success("Campaign updated");
            onSaved?.();
            onClose();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-ink/50 p-3 md:p-6" data-testid="campaign-edit-modal" onClick={onClose}>
            <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b border-stone-200 px-6 py-4">
                    <div className="font-heading text-lg font-bold">Edit Campaign</div>
                    <button onClick={onClose} data-testid="edit-close" className="rounded-full p-1.5 text-stone2 hover:bg-stone-100"><X size={20} /></button>
                </div>
                <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
                    <div>
                        <label className={lbl}>Campaign Name</label>
                        <input data-testid="edit-name" value={form.name} onChange={(e) => set({ name: e.target.value })} className={inputCls} />
                    </div>
                    <div>
                        <label className={lbl}>Description</label>
                        <textarea data-testid="edit-description" value={form.description} onChange={(e) => set({ description: e.target.value })} rows={2} className={`${inputCls} resize-none`} />
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                            <label className={lbl}>Purpose</label>
                            <select data-testid="edit-purpose" value={form.purpose} onChange={(e) => set({ purpose: e.target.value })} className={inputCls}>
                                {PURPOSES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className={lbl}>Dialing Mode</label>
                            <select data-testid="edit-mode" value={form.dialing_mode} onChange={(e) => set({ dialing_mode: e.target.value })} className={inputCls}>
                                {DIAL_MODES.filter((m) => m.ready).map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className={lbl}>Assigned Agents</label>
                        <div className="mt-1.5 flex flex-wrap gap-2" data-testid="edit-agents">
                            {agents.length === 0 && <span className="text-sm text-stone2">No agents in this workspace.</span>}
                            {agents.map((a) => {
                                const on = form.agent_ids.includes(a.id);
                                return (
                                    <button key={a.id} type="button" data-testid={`edit-agent-${a.id}`} onClick={() => toggleAgent(a.id)}
                                        className={`rounded-full border px-3 py-1.5 text-sm font-semibold transition-all ${on ? "border-ink bg-ink text-white" : "border-stone-300 text-ink hover:border-ink"}`}>
                                        {a.name || a.email}
                                    </button>
                                );
                            })}
                        </div>
                        <p className="mt-1 text-xs text-stone2">Only assigned agents see this campaign in their console.</p>
                    </div>
                    <div className="flex items-center justify-between rounded-xl border border-stone-200 px-4 py-3">
                        <div>
                            <div className="text-sm font-semibold text-ink">Call Recording</div>
                            <div className="text-xs text-stone2">Record calls placed in this campaign.</div>
                        </div>
                        <Switch data-testid="edit-recording" checked={form.recording_enabled} onCheckedChange={(v) => set({ recording_enabled: v })} />
                    </div>
                    <div className="rounded-xl border border-stone-200 px-4 py-3">
                        <div className="flex items-center justify-between">
                            <div className="text-sm font-semibold text-ink">Working Hours</div>
                            <Switch data-testid="edit-wh-enabled" checked={form.wh_enabled} onCheckedChange={(v) => set({ wh_enabled: v })} />
                        </div>
                        {form.wh_enabled && (
                            <div className="mt-3 grid grid-cols-2 gap-3">
                                <div><label className={lbl}>Open</label><input type="time" data-testid="edit-wh-open" value={form.wh_open} onChange={(e) => set({ wh_open: e.target.value })} className={inputCls} /></div>
                                <div><label className={lbl}>Close</label><input type="time" data-testid="edit-wh-close" value={form.wh_close} onChange={(e) => set({ wh_close: e.target.value })} className={inputCls} /></div>
                            </div>
                        )}
                    </div>
                    <div>
                        <label className={lbl}>Retry attempts per record</label>
                        <input type="number" min={0} max={10} data-testid="edit-retry" value={form.retry_attempts} onChange={(e) => set({ retry_attempts: e.target.value })} className={inputCls} />
                    </div>
                    {form.dialing_mode === "predictive" && (
                        <div>
                            <label className={lbl}>Predictive pacing ratio (lines per available agent)</label>
                            <input type="number" step="0.5" min={1} max={5} data-testid="edit-pacing-ratio" value={form.pacing_ratio} onChange={(e) => set({ pacing_ratio: e.target.value })} className={inputCls} />
                            <p className="mt-1 text-xs text-stone2">Higher = more talk time but higher chance of dropped (abandoned) calls.</p>
                        </div>
                    )}
                    {form.dialing_mode === "predictive" && (
                        <div>
                            <label className={lbl}>Abandon-rate cap (%)</label>
                            <input type="number" step="1" min={1} max={100} data-testid="edit-abandon-threshold"
                                value={Math.round((Number(form.abandon_threshold) || 0.03) * 100)}
                                onChange={(e) => set({ abandon_threshold: (Number(e.target.value) || 0) / 100 })} className={inputCls} />
                            <p className="mt-1 text-xs text-stone2">If dropped calls exceed this cap, the dialer auto-throttles its pacing (most markets require ≤ 3%).</p>
                        </div>
                    )}
                    {form.dialing_mode === "predictive" && (
                        <div>
                            <label className={lbl}>Abandoned-call message (played when no agent is free)</label>
                            <textarea rows={2} data-testid="edit-abandon-message"
                                value={form.abandon_message}
                                placeholder="We're sorry, all of our agents are currently busy. We'll call you back shortly. Thank you for your patience."
                                onChange={(e) => set({ abandon_message: e.target.value })} className={inputCls} />
                            <p className="mt-1 text-xs text-stone2">Spoken to the customer before hangup, then the lead is re-queued for a callback. Leave blank to use the default.</p>
                        </div>
                    )}
                    <div className="rounded-xl border border-stone-200 p-4">
                        <div className="mb-2 font-heading text-sm font-bold text-ink">Agent Script</div>
                        <ScriptEditor
                            talkingPoints={form.talking_points}
                            objections={form.objections}
                            onChange={({ talking_points, objections }) => set({ talking_points, objections })}
                            purpose={form.purpose}
                            name={form.name}
                            description={form.description}
                        />
                    </div>
                    {isSf && (
                        <div className="rounded-xl border border-stone-200">
                            <button type="button" data-testid="edit-filter-toggle" onClick={() => setShowFilter((s) => !s)}
                                className="flex w-full items-center justify-between px-4 py-3 text-left">
                                <div>
                                    <div className="text-sm font-semibold text-ink">{isPickerSf ? "Salesforce Source" : "Salesforce Filter"}</div>
                                    <div className="text-xs text-stone2">{isPickerSf ? "Change the list view / report this campaign pulls from, then use “Refresh from Salesforce” to apply." : "Edit which records this campaign pulls, then use “Refresh from Salesforce” to apply."}</div>
                                </div>
                                {showFilter ? <CaretDown size={16} /> : <CaretRight size={16} />}
                            </button>
                            {showFilter && (
                                <div className="border-t border-stone-200 p-4">
                                    {isFilterSf && <SfFilterBuilder kind={kind} filters={filters} onChange={setFilters} />}
                                    {isPickerSf && <SfSourcePicker kind={kind} value={dataSource} onChange={(patch) => setDataSource((d) => ({ ...d, ...patch }))} />}
                                    <p className="mt-2 text-xs text-stone2">Saving updates the {isPickerSf ? "source" : "filter"} only. Click <span className="font-semibold">Refresh from Salesforce</span> on the campaign to pull newly-matching records.</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex justify-end gap-2 border-t border-stone-200 px-6 py-4">
                    <button onClick={onClose} className="rounded-full border border-stone-300 px-4 py-2 text-sm font-semibold hover:border-ink">Cancel</button>
                    <button onClick={save} disabled={saving} data-testid="edit-save" className="rounded-full bg-ink px-5 py-2 text-sm font-semibold text-white disabled:opacity-50">{saving ? "Saving…" : "Save changes"}</button>
                </div>
            </div>
        </div>
    );
}
